/**
 * v2.2 P1.5: URLs in a note become links, and nothing else in the note becomes anything.
 *
 * No HTML is ever rendered and no markdown beyond the link itself: the note is split into text and
 * link parts, and React renders the text parts as text. Only http(s) URLs (and bare `www.`, which is
 * given https) are linked — a `javascript:` or `data:` URL stays plain text however it is written, so
 * a pasted string can never run in the page.
 */
export type NotePart = { kind: "text"; text: string } | { kind: "link"; text: string; href: string };

// http(s)://… or www.… up to whitespace; trailing punctuation that usually ends a sentence is trimmed.
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;
const TRAILING = /[.,;:!?)\]}'"]+$/;

export function safeHref(candidate: string): string | null {
  const href = candidate.toLowerCase().startsWith("www.") ? `https://${candidate}` : candidate;
  try {
    const url = new URL(href);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

export function linkifyParts(text: string): NotePart[] {
  const parts: NotePart[] = [];
  let last = 0;
  for (const match of text.matchAll(URL_RE)) {
    const start = match.index;
    let raw = match[0];
    const trail = TRAILING.exec(raw);
    if (trail) raw = raw.slice(0, raw.length - trail[0].length);
    const href = raw ? safeHref(raw) : null;
    if (!href) continue;
    if (start > last) parts.push({ kind: "text", text: text.slice(last, start) });
    parts.push({ kind: "link", text: raw, href });
    last = start + raw.length;
  }
  if (last < text.length) parts.push({ kind: "text", text: text.slice(last) });
  return parts;
}
